import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import {
    Box,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import searchIcon from "../../assets/images/icons/searchIcon.svg";
import CustomTable from "../../components/customTable";
import DateRangePickerComponent from "../../components/date/DateRangePickerComponent";
import useDebounce from "../../hooks/use.debounce";
import { useGetApiLogStatsQuery, useGetApiLogsQuery } from "../../redux/api";
import { CustomSearchInput, FORM_SELECTFIELD_STYLES } from "../../utils/constant";
import ApiLogDetailDrawer from "./components/ApiLogDetailDrawer";
import { Card, PageBody, PageHeader, StatTile } from "./components/SecurityShell";
import {
    HTTP_METHODS,
    SIDES,
    STATUS_GROUPS,
    StatusChip,
    USER_TYPES,
    compactParams,
    formatDateTime,
    formatMs,
    routeLabel,
    toApiDateTime,
} from "./securityUtils";

// FORM_SELECTFIELD_STYLES sets width:100%, which would stack the filters one per
// row — override it so they flow inline and wrap only when they run out of space.
const selectSx = { ...FORM_SELECTFIELD_STYLES, maxHeight: "48px", width: "auto", minWidth: "160px" };
const EMPTY_FILTERS = {
    search: "", method: "", side: "", userType: "", statusGroup: "",
    routePath: "", statusCode: "",
};

/**
 * Every API call the backend has logged, filterable by endpoint and by time.
 * Read-only — this is a monitoring view, not an administration tool.
 */
const ApiLogs = () => {
    // Deep links such as ?statusCode=429 from the Rate Limits page seed the
    // filters once; from then on they are ordinary state, so Reset clears them.
    const [searchParams] = useSearchParams();

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [filters, setFilters] = useState({
        ...EMPTY_FILTERS,
        statusCode: searchParams.get("statusCode") || "",
        statusGroup: searchParams.get("statusGroup") || "",
    });
    // Times are separate from the date range because react-datepicker only gives
    // us whole days; together they form the `YYYY-MM-DD HH:mm:ss` the API takes.
    const [times, setTimes] = useState({ start: "", end: "" });
    const [range, setRange] = useState({ from: null, to: null });
    const [detailId, setDetailId] = useState(null);

    const debouncedSearch = useDebounce(filters.search, 500);

    const queryFilters = useMemo(() => compactParams({
        search: debouncedSearch,
        method: filters.method,
        side: filters.side,
        userType: filters.userType,
        statusGroup: filters.statusGroup,
        routePath: filters.routePath,
        statusCode: filters.statusCode,
        startDate: toApiDateTime(range.from, times.start, "start"),
        endDate: toApiDateTime(range.to, times.end, "end"),
    }), [debouncedSearch, filters, range, times]);

    const { data, isFetching } = useGetApiLogsQuery({
        ...queryFilters,
        limit,
        offset: page * limit,
    });
    const { data: statsData } = useGetApiLogStatsQuery(queryFilters);

    const summary = statsData?.result?.summary;
    const routes = statsData?.result?.routes || [];
    const logs = data?.result?.logs || [];
    const total = data?.result?.total || 0;

    const setFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(0);
    };

    const resetAll = () => {
        setFilters({ ...EMPTY_FILTERS });
        setTimes({ start: "", end: "" });
        setRange({ from: null, to: null });
        setPage(0);
    };

    const errorRate = summary?.totalCalls
        ? `${((Number(summary.errorCount) / Number(summary.totalCalls)) * 100).toFixed(1)}%`
        : "0%";

    // Column keys are snake_case / single words because CustomTable derives the
    // header from them by splitting on "_" and capitalising each word.
    const cols = ["time", "method", "endpoint", "status", "user", "ip_address", "duration"];
    const tableData = logs.map((log) => ({
        id: log.id,
        time: formatDateTime(log.createdDate),
        method: log.method,
        endpoint: (
            <Tooltip title={log.url || ""} arrow>
                <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {log.routePath || log.url || "-"}
                </span>
            </Tooltip>
        ),
        status: <StatusChip code={log.statusCode} />,
        user: log.userId ? `#${log.userId} ${log.userType || ""}`.trim() : "Anonymous",
        ip_address: log.ipAddress || "-",
        duration: formatMs(log.durationMs),
    }));

    const Action = (row) => (
        <div className="actBtnGrps">
            <span className="viewIcon" onClick={() => setDetailId(row.id)}>View</span>
        </div>
    );

    return (
        <>
            <Stack direction="column" gap={2}>
                <PageHeader
                    title="API Logs"
                    subtitle="Every request the API has served. Filter by endpoint, status, user or time window."
                    actions={
                        <CustomSearchInput
                            className="inputSearch sContrl p-0"
                            value={filters.search}
                            placeholder="Search URL or route..."
                            handleSearch={(e) => setFilter("search", e.target.value)}
                            clearSearch={() => setFilter("search", "")}
                            startAdornment={
                                <InputAdornment className="overIcon" position="start"
                                    sx={{ maxHeight: "none", height: "38px", minWidth: "38px", marginRight: "0px", background: "none !important", borderRadius: "50px !important" }}>
                                    <img src={searchIcon} height={20} width={20} alt="" />
                                </InputAdornment>
                            }
                        />
                    }
                />

                <PageBody>
                    <Stack direction="row" gap={2} flexWrap="wrap">
                        <StatTile label="Total calls" value={Number(summary?.totalCalls || 0).toLocaleString()} hint="in the current filter" />
                        <StatTile label="Error rate" value={errorRate} tone={Number(summary?.errorCount) > 0 ? "warning" : "success"}
                            hint={`${Number(summary?.errorCount || 0).toLocaleString()} responses ≥ 400`} />
                        <StatTile label="Rate limited" value={Number(summary?.rateLimitedCount || 0).toLocaleString()} tone={Number(summary?.rateLimitedCount) > 0 ? "error" : "success"}
                            hint="429 responses" />
                        <StatTile label="Avg response" value={formatMs(summary?.avgDurationMs)} hint={`peak ${formatMs(summary?.maxDurationMs)}`} />
                        <StatTile label="Unique callers" value={Number(summary?.uniqueIps || 0).toLocaleString()} hint={`${Number(summary?.uniqueUsers || 0).toLocaleString()} signed-in users`} />
                    </Stack>

                    {/* overflow:visible so the filter popups (date calendar, time
                        pickers) are not clipped by the card's rounded corners. */}
                    <Card sx={{ padding: "16px", overflow: "visible" }}>
                        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
                            <Select sx={selectSx} displayEmpty value={filters.routePath}
                                onChange={(e) => setFilter("routePath", e.target.value)}>
                                <MenuItem value="">All APIs</MenuItem>
                                {routes.map((r) => (
                                    <MenuItem key={`${r.method}-${r.routePath}`} value={r.routePath}>
                                        {routeLabel(r.method, r.routePath)} ({r.calls})
                                    </MenuItem>
                                ))}
                            </Select>

                            <Select sx={selectSx} displayEmpty value={filters.method}
                                onChange={(e) => setFilter("method", e.target.value)}>
                                <MenuItem value="">All methods</MenuItem>
                                {HTTP_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </Select>

                            <Select sx={selectSx} displayEmpty value={filters.statusGroup}
                                onChange={(e) => setFilter("statusGroup", e.target.value)}>
                                <MenuItem value="">All statuses</MenuItem>
                                {STATUS_GROUPS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                            </Select>

                            <Select sx={selectSx} displayEmpty value={filters.side}
                                onChange={(e) => setFilter("side", e.target.value)}>
                                <MenuItem value="">All sides</MenuItem>
                                {SIDES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>

                            <Select sx={selectSx} displayEmpty value={filters.userType}
                                onChange={(e) => setFilter("userType", e.target.value)}>
                                <MenuItem value="">All user types</MenuItem>
                                {USER_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>

                            <DateRangePickerComponent
                                text="Date range"
                                monthsShown={1}
                                // The calendar renders as an absolutely-positioned
                                // popper, which the Card's overflow:hidden clipped.
                                // A portal lifts it out of that clipping context.
                                portalId="security-datepicker-portal"
                                dateReset={range.from ? { from: range.from } : {}}
                                getSelectedDate={(dates) => {
                                    setRange({ from: dates?.[0] || null, to: dates?.[1] || null });
                                    setPage(0);
                                }}
                            />

                            <TextField
                                type="time" size="small" label="From time" InputLabelProps={{ shrink: true }}
                                value={times.start} sx={{ width: 140 }}
                                onChange={(e) => { setTimes((p) => ({ ...p, start: e.target.value })); setPage(0); }}
                            />
                            <TextField
                                type="time" size="small" label="To time" InputLabelProps={{ shrink: true }}
                                value={times.end} sx={{ width: 140 }}
                                onChange={(e) => { setTimes((p) => ({ ...p, end: e.target.value })); setPage(0); }}
                            />

                            <Tooltip title="Reset all filters" arrow>
                                <RotateLeftIcon className="showhandCursor" onClick={resetAll} sx={{ fontSize: 28, color: "#666" }} />
                            </Tooltip>
                        </Stack>

                        {filters.statusCode && (
                            <Box sx={{ mt: 1.5 }}>
                                <Chip
                                    label={`Status code = ${filters.statusCode}`}
                                    onDelete={() => setFilter("statusCode", "")}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                />
                            </Box>
                        )}

                        {(range.from && !range.to) && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                Pick an end date to apply the range.
                            </Typography>
                        )}
                        {(!range.from && (times.start || times.end)) && (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
                                Times only apply once a date range is selected.
                            </Typography>
                        )}
                    </Card>

                    <Card>
                        <CustomTable
                            // CustomTable keeps its own page state, so remount it when the
                            // filters change — otherwise it stays on a page that no longer exists.
                            key={JSON.stringify(queryFilters)}
                            cols={cols}
                            data={tableData}
                            isCheckbox={false}
                            actionComp={Action}
                            count={total}
                            onPageChange={({ page: p, rows }) => { setPage(p); setLimit(rows); }}
                        />
                        {isFetching && (
                            <Box sx={{ p: 1, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary">Loading…</Typography>
                            </Box>
                        )}
                    </Card>
                </PageBody>
            </Stack>

            <ApiLogDetailDrawer
                logId={detailId}
                open={!!detailId}
                onClose={() => setDetailId(null)}
            />
        </>
    );
};

export default ApiLogs;
